import React, { useState, useCallback } from 'react';
import { FaTwitter, FaWhatsapp } from 'react-icons/fa';
import { MdContentCopy } from 'react-icons/md';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Notice from 'components/atoms/Notice/Notice';
import SocialIcon from 'components/atoms/SocialIcon/SocialIcon';
import { generateFacebookShareUrl } from 'utils/socialShareUtils';
import classes from './SocialShareButtons.module.scss';

export interface SocialShareButtonsProps {
  url: string;
  description: string;
  className?: string;
  type?: 'offer' | 'balance' | 'surveyComplete' | 'noTemplate';
  useDirectUrls?: boolean;
  copyUrlOnly?: boolean;
  tenantConfig?: {
    facebookAppId?: string;
  };
}

const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({
  url,
  description,
  className,
  type = 'noTemplate',
  useDirectUrls = false,
  copyUrlOnly = false,
  tenantConfig,
}) => {
  const [showNotice, setShowNotice] = useState(false);

  const trackShareEvent = useCallback(
    (platform: string) => {
      if (globalThis.window?.dataLayer) {
        globalThis.window.dataLayer.push({
          event: 'share_button_clicked',
          share_platform: platform,
          share_type: type,
          page_path: globalThis.window.location.pathname,
        });
      }
    },
    [type],
  );

  const handleCopyLink = useCallback(async () => {
    try {
      const copyText = copyUrlOnly || type === 'offer' ? url : `${description} ${url}`;
      await navigator.clipboard.writeText(copyText);
      setShowNotice(true);
      setTimeout(() => setShowNotice(false), 3000);
      trackShareEvent('Copy');
    } catch (err) {
      console.error('Failed to copy link: ', err);
    }
  }, [type, url, description, copyUrlOnly, trackShareEvent]);

  const handleFacebookShare = useCallback(() => {
    if (useDirectUrls && tenantConfig?.facebookAppId) {
      const directUrl = generateFacebookShareUrl(url, tenantConfig.facebookAppId);
      globalThis.window?.open(directUrl, '_blank', 'noopener,noreferrer');
    }
    trackShareEvent('Facebook');
  }, [trackShareEvent, useDirectUrls, tenantConfig, url]);

  const handleTwitterShare = useCallback(() => {
    trackShareEvent('Twitter');
  }, [trackShareEvent]);

  const handleWhatsAppShare = useCallback(() => {
    trackShareEvent('WhatsApp');
  }, [trackShareEvent]);

  return (
    <>
      <div className={className}>
        {useDirectUrls ? (
          <button
            className={classes.iconButton}
            onClick={handleFacebookShare}
            aria-label="Share on Facebook"
          >
            <SocialIcon socialProvider="facebook" color="currentColor" />
          </button>
        ) : (
          <FacebookShareButton
            url={url}
            onShareWindowClose={handleFacebookShare}
            className={classes.iconButton}
            aria-label="Share on Facebook"
          >
            <SocialIcon socialProvider="facebook" color="currentColor" />
          </FacebookShareButton>
        )}
        <TwitterShareButton
          url={url}
          title={description}
          onShareWindowClose={handleTwitterShare}
          className={classes.iconButton}
          aria-label="Share on Twitter"
        >
          <FaTwitter />
        </TwitterShareButton>
        <WhatsappShareButton
          url={url}
          title={description}
          onShareWindowClose={handleWhatsAppShare}
          className={classes.iconButton}
          aria-label="Share on WhatsApp"
        >
          <FaWhatsapp />
        </WhatsappShareButton>
        <button className={classes.iconButton} onClick={handleCopyLink} aria-label="Copy link">
          <MdContentCopy />
        </button>
      </div>
      {showNotice && <Notice status="success" message="Link copied to clipboard!" />}
    </>
  );
};

export default SocialShareButtons;
